import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { api } from '../contexts/AuthContext';
import { 
  FileSpreadsheet, 
  Upload,
  File,
  X,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Lightbulb,
  Monitor
} from 'lucide-react';

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      const mobileWidth = window.innerWidth < 768;
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(mobileWidth || (hasTouchScreen && window.innerWidth < 1024));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Fun data cleaning facts and tips to show during upload
const DATA_FACTS = [
  { type: 'fact', text: '📊 Bad data costs businesses an average of $12.9 million annually.' },
  { type: 'tip', text: '💡 Tip: Standardizing email domains can increase deliverability by up to 20%.' },
  { type: 'fact', text: '🔢 On average, 25% of B2B contact data becomes outdated every year.' },
  { type: 'tip', text: '💡 Tip: Normalizing phone numbers to E.164 format improves CRM sync accuracy.' },
  { type: 'fact', text: '🌍 Country name variations ("US", "USA", "United States") cause 15% of CRM duplicates.' },
  { type: 'tip', text: '💡 Tip: Extracting email domains helps identify company affiliations instantly.' },
  { type: 'fact', text: '📈 Clean data can improve email open rates by up to 40%.' },
  { type: 'tip', text: '💡 Tip: Title casing names makes your emails feel more personal.' },
  { type: 'fact', text: '⚡ Data scientists spend 60% of their time cleaning data.' },
  { type: 'tip', text: '💡 Tip: Splitting full names into first/last improves personalization.' },
  { type: 'fact', text: '🎯 Poor data quality costs marketers 21% of their budget.' },
  { type: 'tip', text: '💡 Tip: Standardizing revenue formats enables better segmentation.' },
  { type: 'fact', text: '📱 Incorrect phone formats cause 12% of sales calls to fail.' },
  { type: 'tip', text: '💡 Tip: Fixing ".con" to ".com" typos saves bounce rates.' },
];

const UploadPage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  // Rotate through facts during upload
  useEffect(() => {
    if (uploading) {
      const interval = setInterval(() => {
        setCurrentFactIndex(prev => (prev + 1) % DATA_FACTS.length);
      }, 5000); // Show each fact for 5 seconds
      return () => clearInterval(interval);
    }
  }, [uploading]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError(null);
    
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-invalid-type') {
        setError('Please upload an Excel (.xlsx, .xls) or CSV file');
      } else if (rejection.errors[0]?.code === 'file-too-large') {
        setError('File is too large. Maximum size is 50MB');
      }
      return;
    }
    
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      setUploadResult(null);
      
      // Auto-upload immediately after file is selected
      uploadFile(selectedFile);
    }
  }, []);
  
  const [uploadPhase, setUploadPhase] = useState('idle'); // idle, uploading, processing, complete
  
  const uploadFile = async (fileToUpload) => {
    if (!fileToUpload) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadPhase('uploading');
    setError(null);

    const formData = new FormData();
    formData.append('file', fileToUpload);

    // Calculate timeout based on file size (minimum 60s, add 30s per 5MB)
    const fileSizeMB = fileToUpload.size / (1024 * 1024);
    const timeoutMs = Math.max(60000, 60000 + Math.ceil(fileSizeMB / 5) * 30000);
    
    // Track actual upload start time for better progress estimation
    const uploadStartTime = Date.now();
    let lastProgressUpdate = 0;

    try {
      const response = await api.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: timeoutMs,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (progressEvent) => {
          const now = Date.now();
          const elapsed = now - uploadStartTime;
          
          if (progressEvent.total && progressEvent.total > 0) {
            // Calculate raw progress (0-100)
            const rawProgress = (progressEvent.loaded / progressEvent.total) * 100;
            
            // Scale to 0-80% for upload phase (leave 20% for server processing)
            const scaledProgress = Math.round(rawProgress * 0.8);
            
            // Only update if progress increased (avoid jumps backward)
            if (scaledProgress > lastProgressUpdate) {
              lastProgressUpdate = scaledProgress;
              setUploadProgress(scaledProgress);
            }
            
            // When upload reaches 100%, switch to processing phase
            if (rawProgress >= 99.5) {
              setUploadPhase('processing');
              // Start processing animation from 80%
              let processingProgress = 80;
              const processingInterval = setInterval(() => {
                processingProgress += 1;
                if (processingProgress < 95) {
                  setUploadProgress(processingProgress);
                } else {
                  clearInterval(processingInterval);
                }
              }, 500); // Slow increment during processing
              
              // Store interval ID for cleanup
              window._processingInterval = processingInterval;
            }
          } else {
            // Fallback: estimate based on loaded bytes and time
            const loadedMB = progressEvent.loaded / (1024 * 1024);
            const estimatedProgress = Math.min(75, Math.round(loadedMB * 2));
            if (estimatedProgress > lastProgressUpdate) {
              lastProgressUpdate = estimatedProgress;
              setUploadProgress(estimatedProgress);
            }
          }
        }
      });

      // Clear any processing interval
      if (window._processingInterval) {
        clearInterval(window._processingInterval);
      }
      
      // Complete!
      setUploadProgress(100);
      setUploadPhase('complete');
      
      // Check if file was truncated (very large file)
      if (response.data.was_truncated) {
        const processedRows = response.data.processed_row_count || 0;
        const originalRows = response.data.original_row_count || 0;
        toast.warning(
          `Processing ${processedRows.toLocaleString()} of ${originalRows.toLocaleString()} rows.`,
          { duration: 6000 }
        );
      } else {
        toast.success('File uploaded successfully!');
      }
      
      // Navigate directly to cleaning page for seamless UX
      navigate(`/clean/${response.data.file_id}`, { 
        state: { fileData: response.data } 
      });
    } catch (err) {
      // Clear any processing interval on error
      if (window._processingInterval) {
        clearInterval(window._processingInterval);
      }
      
      console.error('Upload error:', err);
      const errorData = err.response?.data?.detail;
      
      // Handle timeout specifically
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError({
          message: 'Upload timed out. Your file may be too large.',
          upgrade_required: false,
          suggest_splitter: true
        });
        toast.error('Upload timed out for large file');
      }
      // Check if it's a limit error with upgrade_required flag
      else if (errorData && typeof errorData === 'object' && errorData.upgrade_required) {
        setError({
          message: errorData.message,
          upgrade_required: true,
          suggest_splitter: false,
          remaining_rows: errorData.remaining_rows,
          file_rows: errorData.file_rows,
          max_per_file: errorData.max_per_file
        });
        toast.error(errorData.message);
      } else {
        // Handle any other error format
        let errorMessage = 'Failed to upload file';
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData && typeof errorData === 'object') {
          // Object error - extract message safely
          errorMessage = errorData.message || errorData.error || errorData.detail || 'Failed to upload file';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        // Suggest splitter for any upload failure with large files
        const isLargeFile = file && file.size > 5 * 1024 * 1024; // 5MB+
        setError({ 
          message: String(errorMessage),  // Ensure it's a string
          upgrade_required: false,
          suggest_splitter: isLargeFile
        });
        toast.error(String(errorMessage).slice(0, 100));  // Truncate long messages
      }
    } finally {
      setUploading(false);
      setUploadPhase('idle');
    }
  };

  const handleUpload = async () => {
    uploadFile(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: false
  });

  const handleContinue = () => {
    if (uploadResult) {
      navigate(`/clean/${uploadResult.file_id}`, { 
        state: { fileData: uploadResult } 
      });
    }
  };

  const removeFile = () => {
    setFile(null);
    setUploadResult(null);
    setError(null);
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-heading font-bold text-slate-900">CRM Data Cleaner</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-soft">
          <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">
            Upload Your File
          </h1>
          <p className="text-slate-600 mb-8">
            Upload an Excel (.xlsx, .xls) or CSV file to start cleaning your data.
          </p>

          {/* Mobile Block Message */}
          {isMobile ? (
            <div 
              className="p-8 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50"
              data-testid="mobile-upload-blocked"
            >
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-indigo-100 text-indigo-600">
                  <Monitor className="w-8 h-8" />
                </div>
                <p className="text-lg font-semibold text-slate-900 mb-2">
                  Desktop Required
                </p>
                <p className="text-sm text-slate-600 mb-4 max-w-xs mx-auto">
                  Our data cleaning tool works best on desktop. Please switch to a computer to upload and clean your files.
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
                  <Monitor className="w-4 h-4" />
                  Open on desktop to continue
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Dropzone */}
              {!uploadResult && (
                <div
                  {...getRootProps()}
                  className={`dropzone ${isDragActive ? 'active' : ''}`}
                  data-testid="dropzone"
                >
                  <input {...getInputProps()} data-testid="file-input" />
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-700 mb-2">
                    {isDragActive ? 'Drop your file here' : 'Drag & drop your file here'}
                  </p>
                  <p className="text-slate-500 mb-4">
                    or click to browse
                  </p>
                  <p className="text-sm text-slate-400">
                    Supports Excel (.xlsx, .xls) and CSV files up to 50MB
                  </p>
                </div>
              )}

              {/* Selected File */}
              {file && !uploadResult && (
                <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <File className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{file.name}</p>
                        <p className="text-sm text-slate-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={removeFile}
                  className="text-slate-400 hover:text-slate-600"
                  data-testid="remove-file-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {uploading && (
                <div className="mt-4">
                  <Progress value={uploadProgress} className="h-2" />
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      {uploadPhase === 'uploading' && (
                        <>
                          <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
                          <p className="text-sm text-slate-600">
                            Uploading... {uploadProgress}%
                          </p>
                        </>
                      )}
                      {uploadPhase === 'processing' && (
                        <>
                          <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                          <p className="text-sm text-amber-600">
                            Processing your file... {uploadProgress}%
                          </p>
                        </>
                      )}
                      {uploadPhase === 'complete' && (
                        <>
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <p className="text-sm text-emerald-600">
                            Complete!
                          </p>
                        </>
                      )}
                    </div>
                    {uploadPhase === 'uploading' && (
                      <p className="text-xs text-slate-400">
                        {file.size > 5 * 1024 * 1024 ? 'Large file - this may take a minute' : 'Almost there...'}
                      </p>
                    )}
                    {uploadPhase === 'processing' && (
                      <p className="text-xs text-amber-500">
                        Parsing {Math.round(file.size / 1024).toLocaleString()} KB...
                      </p>
                    )}
                  </div>
                  
                  {/* Fun fact during upload */}
                  <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100 transition-all duration-300">
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-indigo-700 leading-relaxed">
                        {DATA_FACTS[currentFactIndex].text}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-3 text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="flex-1">
                  {typeof error === 'string' 
                    ? error 
                    : error?.message || 'An error occurred. Please try again.'}
                </p>
              </div>
              
              {/* Show remaining rows info */}
              {error?.remaining_rows !== undefined && error?.remaining_rows >= 0 && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Your remaining capacity:</strong> {error.remaining_rows.toLocaleString()} rows this month
                  </p>
                  {error.file_rows && (
                    <p className="text-sm text-blue-600 mt-1">
                      Your file has {error.file_rows.toLocaleString()} rows. Please upload a file with ≤{error.remaining_rows.toLocaleString()} rows.
                    </p>
                  )}
                </div>
              )}
              
              {error?.upgrade_required && error?.remaining_rows > 0 && (
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button 
                    onClick={() => navigate('/tools/split')}
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-50"
                    data-testid="split-file-btn"
                  >
                    Split File (Free)
                  </Button>
                </div>
              )}
              {error?.suggest_splitter && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 mb-3">
                    <strong>Tip:</strong> Large files can sometimes fail due to network issues. 
                    Try splitting your file into smaller parts first.
                  </p>
                  <Button 
                    onClick={() => navigate('/tools/split')}
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    data-testid="go-to-splitter-btn"
                  >
                    Open File Splitter (Free)
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div className="mt-6 p-6 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
                <h3 className="font-semibold text-emerald-900">File Uploaded Successfully!</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-emerald-700">File Name</p>
                  <p className="font-medium text-emerald-900">{uploadResult.original_name}</p>
                </div>
                <div>
                  <p className="text-emerald-700">File Type</p>
                  <p className="font-medium text-emerald-900 uppercase">{uploadResult.file_type}</p>
                </div>
                <div>
                  <p className="text-emerald-700">Rows</p>
                  <p className="font-medium text-emerald-900">{uploadResult.row_count.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-emerald-700">Columns</p>
                  <p className="font-medium text-emerald-900">{uploadResult.column_count}</p>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-sm text-emerald-700 mb-2">Columns detected:</p>
                <div className="flex flex-wrap gap-2">
                  {uploadResult.columns.map((col, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-mono"
                    >
                      {col}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4 mt-8">
            {file && !uploadResult && (
              <Button
                onClick={handleUpload}
                disabled={uploading}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload File
                  </>
                )}
              </Button>
            )}

            {uploadResult && (
              <>
                <Button
                  variant="outline"
                  onClick={removeFile}
                  className="rounded-full px-6"
                  data-testid="upload-another-btn"
                >
                  Upload Another
                </Button>
                <Button
                  onClick={handleContinue}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8"
                  data-testid="continue-btn"
                >
                  Continue to Cleaning
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}
          </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default UploadPage;
